import jwtDecode from "jwt-decode";
import authService from "@/services/auth";
import configService from "@/services/config";
import store from "@/store";
import { USER_ROLES } from "@/services/constants";

/**
 * @description Rutas permitidas para usuarios con rol paciente.
 *              Cualquier otra ruta redirige a patient-me.
 */
const PATIENT_ALLOWED_ROUTES = [
  "home",
  "patient-me",
  "patient-my-tasks",
  "patient-recommendations",
  "patient-reports",
  "patient-dashboard",
  "patient-prescription-request",
  "patient-turnos",
  "patient-credential",
  "prescription-purchase",
  "patient-monitoring",
  "my-procedures",
  "patient-pharmacy-duty",
  "profile",
];

export function verifyToken(to, from, next) {
  if (to.name === "login") {
    authService.clearToken();
    return next();
  }

  if (to.meta.isPublic) {
    return next();
  }

  const token = authService.getToken();
  if (!token) {
    authService.clearToken();
    const query = to.path === "/" ? {} : { redirectUrl: to.path };
    return next({ name: "login", query });
  }

  const decodedToken = jwtDecode(token);
  const isExpiredToken = Math.floor(Date.now() / 1000) > decodedToken.exp;
  const user = store.state.user;
  const isPatient =
    user.role === USER_ROLES.PATIENT.name ||
    user.role === USER_ROLES.PATIENT.alias;

  if (isExpiredToken) {
    authService.clearToken();
    return next({ name: "login" });
  }

  if (!isPatient && !decodedToken.aud.includes(configService.audience)) {
    authService.clearToken();
    return next({ name: "login" });
  }

  if (
    configService.organizationId &&
    decodedToken.organization !== configService.organizationId
  ) {
    authService.clearToken();
    return next({
      name: "error",
      query: {
        code: 401,
        message: "Tu usuario no pertenece a esta organización.",
      },
    });
  }

  next();
}

/**
 * @description Guard que restringe a pacientes a sus rutas permitidas.
 *              Si intenta acceder a una ruta no permitida, redirige a patient-me.
 */
export function patientRouteGuard(to, from, next) {
  if (to.meta.isPublic) return next();

  const user = store.state.user;
  const isPatient =
    user.role === USER_ROLES.PATIENT.name ||
    user.role === USER_ROLES.PATIENT.alias;

  if (isPatient && !PATIENT_ALLOWED_ROUTES.includes(to.name)) {
    return next({ name: "patient-me" });
  }

  next();
}

export function trackPage(to, from, next) {
  if (window.analytics) {
    // TODO: Change window.title dynamically
    const { fullPath, params, query } = to;
    window.analytics.page({
      path: fullPath,
      params,
      query,
      tenant: configService.theme,
      url: `${window.location.protocol}://${window.location.host}${fullPath}`,
    });
  }

  next();
}

/**
 * Conditional route guard that enables/disables routes based on meta conditions
 *
 * Usage in route definition:
 * {
 *   path: "/some-route",
 *   name: "some-route",
 *   meta: {
 *     condition: () => configService.enableChat, // Function that returns boolean
 *     // OR
 *     enabled: configService.enableChat, // Direct boolean value
 *     // Optional: redirect to this route if condition fails
 *     redirectTo: { name: "home" } // or { path: "/home" }
 *   },
 *   component: SomeComponent
 * }
 */
export function conditionalRoute(to, from, next) {
  // Check all matched routes (parent and children) for condition/enabled meta
  // to.matched contains all route records from root to current route
  const matchedRoute = to.matched.find(
    (route) =>
      route.meta &&
      (route.meta.condition !== undefined || route.meta.enabled !== undefined)
  );

  // If no route in the matched chain has condition/enabled, allow access
  if (!matchedRoute) {
    return next();
  }

  const meta = matchedRoute.meta;
  let isEnabled = false;

  // Check if condition is a function
  if (typeof meta.condition === "function") {
    try {
      isEnabled = meta.condition(to, from);
    } catch (error) {
      console.error("Error evaluating route condition:", error);
      isEnabled = false;
    }
  }
  // Check if enabled is a boolean
  else if (typeof meta.enabled === "boolean") {
    isEnabled = meta.enabled;
  }
  // If condition is a boolean (backward compatibility)
  else if (typeof meta.condition === "boolean") {
    isEnabled = meta.condition;
  }

  // If route is enabled, allow access
  if (isEnabled) {
    return next();
  }

  // Route is disabled - handle redirect or block
  if (meta.redirectTo) {
    // Redirect to specified route
    return next(meta.redirectTo);
  }

  // Default: redirect to home if no redirect specified
  return next({ name: "home" });
}
